import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { CalendarIcon, Users, ExternalLink, Clock, Trash2, AlertTriangle } from 'lucide-react';
import type { WaitlistEntryAdmin } from '@/types/booking';
import type { GroupClassProviderOption } from '@/services/api/admin-availability.service';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import { Spinner } from '@/components/ui/spinner';
import { endTimeFromStart } from '@/utils/time';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';

import {
  CancelGroupClassScopeChoice,
  type GroupClassCancelScope,
} from '@/components/availability/CancelGroupClassScopeChoice';

export type { GroupClassCancelScope };

// Loose shape used by both admin & staff variants — same fields, different
// type names. We accept the structural minimum so this dialog stays neutral
// to who's calling it.
export interface GroupClassSlotDetailLike {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  service: { id: number; name: string; duration_minutes: number };
  provider_id?: number;
  provider?: { id: number; display_name: string | null };
  max_capacity: number;
  current_bookings: number;
  waitlist_enabled?: boolean;
  waitlist_count?: number;
  // Recurrence context: whether this class came from a recurring rule and how
  // many occurrences (this one included) "cancel all following" would take out.
  recurring?: boolean;
  series_remaining?: number;
  participants: Array<{
    id: number;
    public_id: string;
    status: string;
    customer_name: string;
    customer_email: string;
    created_at: string;
  }>;
}

interface EditGroupClassSlotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Slot detail (already fetched). When null+open=true the dialog renders a
  // loading skeleton — lets the parent show the dialog immediately while the
  // detail request is in flight.
  detail: GroupClassSlotDetailLike | null;
  isLoading?: boolean;
  onSave: (params: { date: string; start_time: string; provider_id?: number }) => Promise<void>;
  onCancelClass: (scope: GroupClassCancelScope) => Promise<void>;
  // Optional: when supplied, the Details tab shows a provider picker. The
  // loader is called whenever the prospective date/start_time changes so the
  // dialog can flag which providers are free. Omit it (e.g. staff) to keep the
  // provider read-only — the name still shows.
  loadProviderOptions?: (params: { date: string; start_time: string }) => Promise<GroupClassProviderOption[]>;
  isSaving?: boolean;
  isCancelling?: boolean;
  // Optional: when supplied, the participants list shows a "View" link that
  // navigates to the booking detail. Receives the booking's public_id.
  onViewParticipant?: (publicId: string) => void;
  // Optional: when supplied, the dialog header shows an "Open full page"
  // link that navigates to the dedicated group-class show page.
  onOpenFullPage?: (slotId: number) => void;
  // Optional waitlist data + handler. When the slot's service has waitlist
  // enabled the dialog shows a 3rd tab listing entries with a Remove action.
  waitlistEntries?: WaitlistEntryAdmin[];
  isLoadingWaitlist?: boolean;
  onRemoveWaitlistEntry?: (entryId: number) => Promise<void>;
}

export function EditGroupClassSlotDialog({
  open,
  onOpenChange,
  detail,
  isLoading = false,
  onSave,
  onCancelClass,
  isSaving = false,
  isCancelling = false,
  onViewParticipant,
  onOpenFullPage,
  waitlistEntries,
  isLoadingWaitlist = false,
  onRemoveWaitlistEntry,
  loadProviderOptions,
}: EditGroupClassSlotDialogProps) {
  const { t } = useTranslation(['staff', 'common', 'waitlist']);
  const [date, setDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<string>('09:00');
  const [providerId, setProviderId] = useState<number | null>(null);
  const [providerOptions, setProviderOptions] = useState<GroupClassProviderOption[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelScope, setCancelScope] = useState<GroupClassCancelScope>('single');
  const [rescheduleConfirmOpen, setRescheduleConfirmOpen] = useState(false);
  const [waitlistRemoveTarget, setWaitlistRemoveTarget] = useState<WaitlistEntryAdmin | null>(null);
  const [isRemovingWaitlist, setIsRemovingWaitlist] = useState(false);

  // Sync local form with the latest detail every time the dialog opens or
  // the detail refreshes after a save — otherwise the form would drift
  // away from the source of truth.
  useEffect(() => {
    if (detail) {
      setDate(parseISO(detail.date));
      setStartTime(detail.start_time);
      setProviderId(detail.provider_id ?? detail.provider?.id ?? null);
    }
  }, [detail]);

  // Refresh provider availability whenever the prospective date/time changes
  // (a provider free at 09:00 may be busy at 10:00). Only when the parent opted
  // into provider editing by supplying a loader.
  useEffect(() => {
    if (!open || !detail || !loadProviderOptions || !date) return;
    let cancelled = false;
    setLoadingProviders(true);
    loadProviderOptions({ date: format(date, 'yyyy-MM-dd'), start_time: startTime })
      .then((opts) => { if (!cancelled) setProviderOptions(opts); })
      .catch(() => { if (!cancelled) setProviderOptions([]); })
      .finally(() => { if (!cancelled) setLoadingProviders(false); });
    return () => { cancelled = true; };
  }, [open, detail, loadProviderOptions, date, startTime]);

  const computedEnd = detail
    ? endTimeFromStart(startTime, detail.service.duration_minutes)
    : '';

  const originalProviderId = detail?.provider_id ?? detail?.provider?.id ?? null;
  // The date/time moving is what triggers participant emails — a provider-only
  // change is silent, so it must not raise the "reschedule & notify" prompt.
  const scheduleChanged = !!detail && (
    !date ||
    format(date, 'yyyy-MM-dd') !== detail.date ||
    startTime !== detail.start_time
  );
  const isDirty = !!detail && (scheduleChanged || providerId !== originalProviderId);

  const hasBookings = (detail?.current_bookings ?? 0) > 0;
  const selectedProviderOption = providerOptions.find((p) => p.id === providerId);
  const selectedProviderUnavailable =
    !!selectedProviderOption && !selectedProviderOption.available && providerId !== originalProviderId;
  const providerName =
    selectedProviderOption?.display_name ?? detail?.provider?.display_name ?? null;

  const performSave = async () => {
    if (!detail || !date) return;
    const providerChanged = providerId != null && providerId !== originalProviderId;
    await onSave({
      date: format(date, 'yyyy-MM-dd'),
      start_time: startTime,
      ...(providerChanged ? { provider_id: providerId } : {}),
    });
  };

  const handleSave = async () => {
    if (!detail || !date) return;
    // Only confirm when participants will actually be emailed (date/time moved).
    if (hasBookings && scheduleChanged) {
      setRescheduleConfirmOpen(true);
      return;
    }
    await performSave();
  };

  // How many classes a "this and all following" would remove (this one included).
  const seriesRemaining = detail?.series_remaining ?? 0;
  const isRecurring = !!detail?.recurring && seriesRemaining > 1;

  const handleCancelClick = () => {
    if (!detail) return;
    // Confirm whenever there is something to weigh up: people to email, or a
    // choice between this class and the rest of the recurring batch.
    if (hasBookings || isRecurring) {
      setCancelScope('single');
      setCancelConfirmOpen(true);
      return;
    }
    onCancelClass('single');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-violet-600" />
            {detail?.service.name ?? t('staff:availability.groupClass.review.title', 'Group class')}
          </DialogTitle>
          {detail && onOpenFullPage && (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 self-start text-xs text-violet-700"
              onClick={() => onOpenFullPage(detail.id)}
            >
              {t('staff:availability.groupClass.openFullPage', 'Open full page')}
              <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          )}
        </DialogHeader>

        {isLoading || !detail ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0">
            <TabsList className={cn('grid w-full', detail.waitlist_enabled ? 'grid-cols-3' : 'grid-cols-2')}>
              <TabsTrigger value="details">
                {t('staff:availability.groupClass.tabs.details', 'Details')}
              </TabsTrigger>
              <TabsTrigger value="participants">
                {t('staff:availability.groupClass.tabs.participants', 'Participants')}
                {detail.current_bookings > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-violet-100 px-1.5 text-[11px] font-semibold text-violet-800 tabular-nums">
                    {detail.current_bookings}
                  </span>
                )}
              </TabsTrigger>
              {detail.waitlist_enabled && (
                <TabsTrigger value="waitlist">
                  {t('staff:availability.groupClass.tabs.waitlist', 'Waitlist')}
                  {(waitlistEntries?.length ?? detail.waitlist_count ?? 0) > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-violet-100 px-1.5 text-[11px] font-semibold text-violet-800 tabular-nums">
                      {waitlistEntries?.length ?? detail.waitlist_count ?? 0}
                    </span>
                  )}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="details" className="flex-1 overflow-y-auto space-y-4 pt-4">
              <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm flex items-center justify-between">
                <span className="text-muted-foreground">
                  {t('staff:availability.groupClass.review.spotsPerClass', '{{count}} spots per class', {
                    count: detail.max_capacity,
                  })}
                </span>
                <span className="font-medium tabular-nums">
                  {detail.current_bookings}/{detail.max_capacity}
                </span>
              </div>

              <div className="space-y-2">
                <Label>{t('staff:availability.groupClass.providerLabel', 'Provider')}</Label>
                {loadProviderOptions ? (
                  <>
                    <Select
                      value={providerId != null ? String(providerId) : undefined}
                      onValueChange={(v) => setProviderId(Number(v))}
                      disabled={loadingProviders && providerOptions.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t('staff:availability.groupClass.providerPlaceholder', 'Select a provider')}
                        >
                          {providerName ?? t('staff:availability.groupClass.providerPlaceholder', 'Select a provider')}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {providerOptions.map((p) => {
                          // The class's current provider is always selectable
                          // (keeping them is never a "conflict"); others are
                          // greyed out when they're busy at the chosen time.
                          const unavailable = !p.available && p.id !== originalProviderId;
                          return (
                            <SelectItem key={p.id} value={String(p.id)} disabled={unavailable}>
                              <span className="flex items-center gap-2">
                                {p.display_name ?? `#${p.id}`}
                                {unavailable && (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-600">
                                    <AlertTriangle className="h-3 w-3" />
                                    {t('staff:availability.groupClass.providerBusy', 'Busy')}
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {selectedProviderUnavailable && (
                      <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        {t(
                          'staff:availability.groupClass.providerUnavailableHint',
                          'This provider has a conflict at the selected time. Pick another time or provider.'
                        )}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm">{providerName ?? '—'}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('staff:availability.dialog.date', 'Date')}</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !date && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date
                        ? format(date, getDateFormats().fullWithWeekday, { locale: getDateLocale() })
                        : t('staff:availability.dialog.pickDate', 'Pick a date')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date ?? undefined}
                      onSelect={(d) => {
                        if (d) setDate(d);
                        setCalendarOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>{t('staff:availability.groupClass.startTimeLabel', 'Start time')}</Label>
                <TimePicker value={startTime} onChange={setStartTime} />
                <p className="text-xs text-muted-foreground tabular-nums">
                  {startTime} → {computedEnd} ·{' '}
                  {t('staff:availability.groupClass.minutesShort', {
                    count: detail.service.duration_minutes,
                    defaultValue: '{{count}} min',
                  })}
                </p>
              </div>

              {hasBookings && (
                <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded">
                  {t(
                    'staff:availability.groupClass.editWithBookingsHint',
                    'Moving the class will email all {{count}} participant(s) about the new time.',
                    { count: detail.current_bookings }
                  )}
                </p>
              )}
            </TabsContent>

            <TabsContent value="participants" className="flex-1 overflow-y-auto pt-4">
              {detail.participants.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  {t(
                    'staff:availability.groupClass.participants.empty',
                    'No bookings yet for this class.'
                  )}
                </div>
              ) : (
                <ul className="space-y-2">
                  {detail.participants.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between rounded-lg border bg-card px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{p.customer_name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {p.customer_email}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className={cn(
                            'text-[11px] font-medium uppercase tracking-wide rounded-full px-2 py-0.5',
                            p.status === 'confirmed' &&
                              'bg-emerald-100 text-emerald-800',
                            p.status === 'pending' &&
                              'bg-amber-100 text-amber-800',
                            p.status === 'completed' &&
                              'bg-slate-100 text-slate-700',
                            p.status === 'no_show' &&
                              'bg-rose-100 text-rose-800'
                          )}
                        >
                          {t(`status.${p.status}`, p.status)}
                        </span>
                        {onViewParticipant && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onViewParticipant(p.public_id)}
                            className="h-7 px-2"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>

            {detail.waitlist_enabled && (
              <TabsContent value="waitlist" className="flex-1 overflow-y-auto pt-4">
                {isLoadingWaitlist ? (
                  <div className="flex justify-center py-8"><Spinner /></div>
                ) : !waitlistEntries || waitlistEntries.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    <Clock className="h-8 w-8 mx-auto mb-2 text-violet-300" />
                    {t('waitlist:admin.empty', 'No one is on the waitlist yet.')}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {waitlistEntries.map((entry, idx) => (
                      <li
                        key={entry.id}
                        className="flex items-center justify-between rounded-lg border bg-card px-3 py-2"
                      >
                        <div className="min-w-0 flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[11px] font-semibold text-violet-800 tabular-nums">
                            {idx + 1}
                          </span>
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{entry.customer_full_name}</div>
                            <div className="text-xs text-muted-foreground truncate">{entry.customer_email}</div>
                          </div>
                        </div>
                        {onRemoveWaitlistEntry && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setWaitlistRemoveTarget(entry)}
                            className="h-7 px-2 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                            aria-label={t('waitlist:admin.removeButton', 'Remove')}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>
            )}
          </Tabs>
        )}

        <AlertDialog
          open={waitlistRemoveTarget !== null}
          onOpenChange={(open) => { if (!open) setWaitlistRemoveTarget(null); }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('waitlist:admin.removeConfirmTitle', 'Remove from waitlist?')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(
                  'waitlist:admin.removeConfirmDescription',
                  '{{name}} will be emailed that they were removed. This cannot be undone.',
                  { name: waitlistRemoveTarget?.customer_full_name ?? '' },
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isRemovingWaitlist}>
                {t('common:actions.cancel', 'Cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-rose-600 hover:bg-rose-700"
                disabled={isRemovingWaitlist}
                onClick={async (e) => {
                  e.preventDefault();
                  if (!waitlistRemoveTarget || !onRemoveWaitlistEntry) return;
                  setIsRemovingWaitlist(true);
                  try {
                    await onRemoveWaitlistEntry(waitlistRemoveTarget.id);
                    setWaitlistRemoveTarget(null);
                  } finally {
                    setIsRemovingWaitlist(false);
                  }
                }}
              >
                {t('waitlist:admin.removeButton', 'Remove')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="destructive"
            onClick={handleCancelClick}
            disabled={isCancelling || !detail}
            className="w-full sm:w-auto"
          >
            {isCancelling
              ? t('common:actions.cancelling', 'Cancelling...')
              : t('staff:availability.groupClass.actions.cancelClass', 'Cancel class')}
          </Button>
          <div className="flex-1" />
          {/* The Dialog header already has an X close affordance on mobile,
              so the redundant footer Close button is hidden below sm. */}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="hidden sm:inline-flex sm:w-auto"
          >
            {t('common:actions.close', 'Close')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isDirty || isSaving || !detail || !date || selectedProviderUnavailable}
            className="w-full sm:w-auto"
          >
            {isSaving
              ? t('common:actions.saving', 'Saving...')
              : t('common:actions.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                'staff:availability.groupClass.cancelConfirm.title',
                'Cancel this group class?'
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {hasBookings
                ? t(
                    'staff:availability.groupClass.cancelConfirm.description',
                    '{{count}} participant(s) will be notified by email. This cannot be undone.',
                    { count: detail?.current_bookings ?? 0 }
                  )
                : t(
                    'staff:availability.groupClass.cancelConfirm.descriptionEmpty',
                    'This cannot be undone.'
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {isRecurring && (
            <CancelGroupClassScopeChoice
              value={cancelScope}
              onChange={setCancelScope}
              seriesRemaining={seriesRemaining}
            />
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('staff:availability.groupClass.cancelConfirm.keep', 'Keep class')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-700"
              onClick={() => {
                setCancelConfirmOpen(false);
                onCancelClass(cancelScope);
              }}
            >
              {hasBookings
                ? t(
                    'staff:availability.groupClass.cancelConfirm.confirm',
                    'Yes, cancel and notify'
                  )
                : t(
                    'staff:availability.groupClass.cancelConfirm.confirmEmpty',
                    'Yes, cancel'
                  )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={rescheduleConfirmOpen} onOpenChange={setRescheduleConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                'staff:availability.groupClass.rescheduleConfirm.title',
                'Reschedule this group class?'
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'staff:availability.groupClass.rescheduleConfirm.description',
                '{{count}} participant(s) will be emailed about the new date and time.',
                { count: detail?.current_bookings ?? 0 }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('common:actions.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setRescheduleConfirmOpen(false);
                await performSave();
              }}
            >
              {t(
                'staff:availability.groupClass.rescheduleConfirm.confirm',
                'Yes, reschedule and notify'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
